// Portfolio Administration — Insurance tab (rebuilt).
// Top-to-bottom: attention strip (lapsed / expiring / upcoming renewals /
// open claims) → required-policy matrix (card click filters the table) →
// active-policies table + drawer/modal CRUD → bottom row: reports card +
// claims card. Permission gating uses Insurance.Verify (server enforces
// authoritatively).

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { Button, Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  useLoanDueDates,
  useLoanInsurance,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { useInsuranceClaims } from "@/hooks/portfolioAdmin/useInsuranceClaims";
import { LoanReportsCard } from "@/components/portfolioAdmin/loanDetail/reports/LoanReportsCard";
import { AttentionStrip, type AttentionChip } from "../common/AttentionStrip";
import { RequiredInsurancePolicyMatrix } from "../insurance/RequiredInsurancePolicyMatrix";
import { ActivePoliciesTable } from "../insurance/ActivePoliciesTable";
import { InsurancePolicyDrawer } from "../insurance/InsurancePolicyDrawer";
import { AddInsurancePolicyModal } from "../insurance/AddInsurancePolicyModal";
import { ClaimsCard } from "../insurance/ClaimsCard";
import { isOpenClaim } from "../insurance/claimLifecycle";
import { isExpiringWithin, isLapsed } from "../insurance/policyStatus";
import { daysUntil } from "../common/colorBand";
import { useDateFormatter } from "@/utils/datetime";
import type {
  InsurancePolicyDto,
  LoanSummaryDto,
} from "@/api/portfolioAdmin/types";

interface InsuranceTabProps {
  loan: LoanSummaryDto;
}

type Filter = "active+expiring" | "active" | "expiring" | "expired" | "all";

const FILTERS: { value: Filter; label: string }[] = [
  { value: "active+expiring", label: "Active + Expiring" },
  { value: "active", label: "Active" },
  { value: "expiring", label: "Expiring in 30 days" },
  { value: "expired", label: "Expired" },
  { value: "all", label: "All" },
];

function applyFilter(
  policies: InsurancePolicyDto[],
  f: Filter,
): InsurancePolicyDto[] {
  return policies.filter((p) => {
    const expired = isLapsed(p);
    const expiring = isExpiringWithin(p, 30);
    const active = !expired;
    switch (f) {
      case "active":
        // Plain Active excludes the expiring-soon subset; "Active +
        // expiring" (default) is the union.
        return active && !expiring;
      case "expiring":
        return expiring;
      case "expired":
        return expired;
      case "active+expiring":
        return active;
      case "all":
        return true;
    }
  });
}

export function InsuranceTab({ loan }: InsuranceTabProps) {
  const auth = useAuth();
  const { tz } = useDateFormatter();
  const canAdd =
    auth.hasPermission("Insurance.Verify") || auth.hasRoleCode("ADMIN");

  const { data, isLoading, isError } = useLoanInsurance(loan.id);
  const policies = useMemo(() => data ?? [], [data]);

  const { data: calendarData } = useLoanDueDates(loan.id);
  const { data: claimsData } = useInsuranceClaims(loan.id);

  const [filter, setFilter] = useState<Filter>("active+expiring");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [openPolicyId, setOpenPolicyId] = useState<string | null>(null);
  // ?action=add-policy (Overview quick action) auto-opens the add modal;
  // the param is consumed once so closing the modal sticks.
  const [params, setParams] = useSearchParams();
  const [addOpen, setAddOpen] = useState(
    () => canAdd && params.get("action") === "add-policy",
  );
  useEffect(() => {
    if (params.get("action") !== "add-policy") return;
    const updated = new URLSearchParams(params);
    updated.delete("action");
    setParams(updated, { replace: true });
  }, [params, setParams]);

  const visible = useMemo(() => {
    const byStatus = applyFilter(policies, filter);
    return typeFilter
      ? byStatus.filter((p) => p.policyType === typeFilter)
      : byStatus;
  }, [policies, filter, typeFilter, tz]);
  const openPolicy = openPolicyId ? policies.find((p) => p.id === openPolicyId) ?? null : null;

  // ---- attention strip ---------------------------------------------
  const chips = useMemo<AttentionChip[]>(() => {
    // NOT `policies.filter(isLapsed)` — `Array.prototype.filter` passes
    // `(element, index, array)`, so the bare reference would bind the row
    // INDEX to `tz`.
    const lapsed = policies.filter((p) => isLapsed(p));
    const expiring = policies.filter((p) => isExpiringWithin(p, 30));
    const renewals = (calendarData ?? []).filter(
      (i) =>
        i.sourceType.startsWith("insurance-") &&
        // Calendar statuses are lowercase server-side ("completed"/"waived").
        !["completed", "waived"].includes(i.status.toLowerCase()) &&
        (daysUntil(i.dueDate) ?? -1) >= 0,
    );
    const openClaims = (claimsData ?? []).filter(isOpenClaim);
    return [
      {
        id: "lapsed",
        label: "Lapsed",
        count: lapsed.length,
        tone: "error",
        items: lapsed.map((p) => ({
          id: p.id,
          title: `${p.policyType}${p.carrierName ? ` — ${p.carrierName}` : ""}`,
          date: p.expirationDate,
        })),
      },
      {
        id: "expiring",
        label: "Expiring ≤30d",
        count: expiring.length,
        tone: "warning",
        items: expiring.map((p) => ({
          id: p.id,
          title: `${p.policyType}${p.carrierName ? ` — ${p.carrierName}` : ""}`,
          date: p.expirationDate,
        })),
      },
      {
        id: "renewals",
        label: "Upcoming renewals",
        count: renewals.length,
        tone: "neutral",
        items: renewals.map((i) => ({
          id: i.id,
          title: i.title,
          owner: i.assignedToUserId,
          date: i.dueDate,
        })),
      },
      {
        id: "open-claims",
        label: "Open claims",
        count: openClaims.length,
        tone: "error",
        items: openClaims.map((c) => ({
          id: c.id,
          title: `${c.policyType} claim${c.claimNumber ? ` #${c.claimNumber}` : ""}`,
          date: c.dateOfLoss,
        })),
      },
    ];
  }, [policies, calendarData, claimsData, tz]);

  if (isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading insurance…</p>
      </Card>
    );
  }
  if (isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0">
          Could not load insurance policies. Refresh to retry.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid="insurance-tab">
      <AttentionStrip chips={chips} testId="insurance-attention-strip" />

      <Card>
        <RequiredInsurancePolicyMatrix
          policies={policies}
          onSelectType={(t) =>
            setTypeFilter((prev) => (prev === t ? null : t))
          }
        />
      </Card>

      <Card>
        <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-3">
          <h3 className="font-display font-bold text-h3 m-0">Active policies</h3>
          <div className="flex items-center gap-space-2 flex-wrap">
            {typeFilter && (
              <button
                type="button"
                onClick={() => setTypeFilter(null)}
                className="inline-flex items-center gap-space-1 px-space-2 py-space-1 rounded-full border border-border-light bg-white-warm text-caption text-gray-mid hover:text-onyx"
                aria-label={`Clear ${typeFilter} filter`}
                data-testid="insurance-type-filter-chip"
              >
                {typeFilter} <X size={12} aria-hidden />
              </button>
            )}
            <select
              className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
              value={filter}
              onChange={(e) => setFilter(e.target.value as Filter)}
              aria-label="Filter policies"
              data-testid="insurance-filter"
            >
              {FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            {canAdd && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setAddOpen(true)}
                data-testid="insurance-add-button"
              >
                <Plus size={14} aria-hidden /> Add insurance policy
              </Button>
            )}
          </div>
        </div>
        <ActivePoliciesTable
          policies={visible}
          onRowClick={(p) => setOpenPolicyId(p.id)}
        />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-4 items-start">
        <LoanReportsCard loan={loan} discipline="Insurance" />
        <ClaimsCard loanId={loan.id} />
      </div>

      {openPolicy && (
        <InsurancePolicyDrawer
          loanId={loan.id}
          policy={openPolicy}
          open={!!openPolicy}
          onClose={() => setOpenPolicyId(null)}
        />
      )}
      {addOpen && (
        <AddInsurancePolicyModal
          loanId={loan.id}
          open={addOpen}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}
